import { ProLayout, ProScreenAdapter, ProScreenAdapterProvider, useScreenAdapterContext } from '@bedrock/eed-react';
import React, { useEffect } from 'react';
import { Computer, ExchangeHorizontal, Globe, Language, Lock, ArrowCircleUpFilled } from '@bedrock/icons-react';
import { getVersionMenus } from './utils';
import './index.less';
import * as api from 'services/javaApi';
import ChangeSupplierModal from '../Header/ChangeSupplierModal';
import ResetPwdModal from '../Header/ResetPwdModal';
import LanguageModal from '../Header/LanguageModal';
import ScreenZoomSetting from '@mpc/common/src/features/ScreenZoomSetting';
import logo_group_external from '@mpc/common/src/images/logo_group_external.png';
import { Tag } from '@bedrock/components';
import { isProdHostName, getOuterTitle, isOuterIpService } from '@mpc/common/src/utils';
import { defaultRootFontSize, removeVendorStorage } from '@mpc/common/src/utils';
import { PartnershipRequestInvoker, supplierEnums } from '@purchase/supplier';
import { getCurSupplier } from './utils';
import { ReactComponent as mpcLogo } from '@mpc/common/src/images/mpc-logo.svg';
import Icon from '@bedrock/icons-react';
import SupplierChoose from '@/containers/Login/SupplierChoose';


interface MenuType {
  default?: boolean;
  key: string;
  name: string;
  children?: MenuType[];
  group_key?: string;
  group_name?: string;
  path?: string;
}

const getCurrPath = (pathname: string, menus: MenuType[]) => {
  const paths = pathname.split('/').slice(1);
  let currMenus = menus;
  let currPath = '';
  paths.forEach((path, index) => {
    const m = currMenus.find((menu: MenuType) => menu.key === path);
    if (m) {
      currPath += `/${m?.path}`;
    }
    if (m && m.children) {
      currMenus = m.children;
    }
  });
  return currPath;
};

/**
 * 当前展示名称：IP 场景按当前版权方 ID（current_copyright_owner_id）匹配版权方列表
 * （vendorsId 已映射为版权方 ID），非 IP 场景走供应商 getCurSupplier（vendor storage 匹配）
 */
const getCurUserDisplayName = (account: any, suppliersList: any[]): { full_name: string } => {
  if (isOuterIpService()) {
    const currentOwnerId =
      (account as any)?.current_copyright_owner_id ?? (account as any)?.currentUser?.current_copyright_owner_id;
    const curOwner = suppliersList.find((v: any) => v.copyrightOwnerId === currentOwnerId);
    return { full_name: curOwner?.vendorName || '' };
  }
  return getCurSupplier(suppliersList);
};

export default function MPCLayout(props: any) {
  const {
    account = {},
    location: { pathname },
    menuCount,
  } = props;
  const { menus = [], suppliersList = [], vendorProcessAudit } = account;
  const { full_name } = getCurUserDisplayName(account, suppliersList);
  const needShowChangeSupplier =
    suppliersList.length > 1 || suppliersList.some((item: any) => item?.tenants?.length > 1);

  const versionMenus = getVersionMenus(menus);
  const currentPath = getCurrPath(pathname, versionMenus);
  const [selectedMenu, setSelectedMenu] = React.useState(currentPath);
  // 左侧导航默认收起：IP 外包场景菜单项少，默认收起节省横向空间（用户可点折叠按钮手动展开）；
  // 非 IP 外包维持按窗口宽度自适应（<1450px 收起）
  const [collapsed, setCollapsed] = React.useState(
    isOuterIpService() ? true : window.innerWidth < 1450
  );
  const adapterConfig = useScreenAdapterContext();
  const [supplierChooseVisible, setSupplierChooseVisible] = React.useState(false);
  const [resetPwdVisible, setResetPwdVisible] = React.useState(false);

  const handleMenuChange = (path: any) => {
    console.log('path', path);
    props.history.push(path);
    setSelectedMenu(path);
  };

  useEffect(() => {
    const handleWindowResize = () => {
      // IP 外包场景默认收起后不随窗口缩放自动改（保留用户手动展开/收起状态）；非 IP 维持 <1450px 收起
      if (isOuterIpService()) return;
      setCollapsed(window.innerWidth < 1450);
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (vendorProcessAudit?.onboarded) {
      props?.getMenuCount?.();
    }
  }, [pathname]);
  versionMenus.forEach((menu: any) => {
    if (
      vendorProcessAudit?.hasCompletedWholeModification !== true &&
      menu.key === 'outCompanyManagement'
    ) {
      menu.title = (
        <>
          {menu.title} <div className="out-company-management-badge-icon" />
        </>
      );
    }
    let count = menuCount?.find?.((v) => v.key === menu?.key)?.count || 0;
    menu.badge = count;
  });

  const handleChangeSupplier = () => {
    setSupplierChooseVisible(true);
  };

  const handleResetPwd = () => {
    setResetPwdVisible(true);
  };


  return (
    // <ProScreenAdapterProvider>
    <ProLayout
      route={{
        currentPath: currentPath,
        onChange: handleMenuChange,
        routes: versionMenus,
      }}>
      <ProLayout.Header
        // logo={logo_group_external}
        logo={
          <span className="flex items-center gap-2">
            <Icon component={mpcLogo} className="text-[28px]" />
            <span className="font-bold text-[16px]">{getOuterTitle()}</span>
          </span>
        }
        name=""
        mode="normal">
        <ProLayout.Header.Left mode="normal">
          {!isProdHostName() && <Tag color="light-grey" size="small" text="非正式环境" />}
        </ProLayout.Header.Left>
        <ProLayout.Header.Center mode="normal"></ProLayout.Header.Center>
        <ProLayout.Header.Right mode="normal">
          <ProLayout.User
            config={{
              user: {
                name: full_name,
                avatar: account.avatar_url,
                id: account.id,
              },
              menu: [
                needShowChangeSupplier && {
                  icon: <ExchangeHorizontal />,
                  title: isOuterIpService() ? "切换版权方" : "切换供应商身份",
                  onClick: handleChangeSupplier,
                  options: [],
                },
                !isOuterIpService() && {
                  icon: <Lock />,
                  title: "修改密码",
                  onClick: handleResetPwd,
                  options: [],
                },
                // {
                //   icon: <Globe />,
                //   title: "偏好设置",
                //   // subTitle: LANGUAGES_MAP[account.language as keyof typeof LANGUAGES_MAP],
                //   onClick: handleLanguageSetting,
                //   options: [],
                // },
                {
                  icon: <Computer />,
                  title: (
                    <ProScreenAdapter {...adapterConfig}>
                      <div>屏幕缩放设置</div>
                    </ProScreenAdapter>
                  ),
                  // subTitle: '⌘ + ,',
                  onClick: () => console.log('屏幕缩放'),
                  options: [],
                },
              ].filter(Boolean),
              // onSwitchUser: () => console.log('switch user'),
              onLogout: () => {
                if (isOuterIpService()) {
                  // IP 场景登出走版权方登出接口（清 cookie），成功后跳转回登录页
                  api.ipCooperationSessionsLogout().then(() => {
                    removeVendorStorage();
                    window.location.hash = '#/login';
                  });
                } else {
                  PartnershipRequestInvoker.getInstance()
                    .authLogout({ keepCurrentRoute: true })
                    .then(() => {
                      removeVendorStorage();
                    });
                }
              },
            }}
          />
        </ProLayout.Header.Right>
      </ProLayout.Header>
      <ProLayout.Content
        mode="normal"
        renderMenuItem={undefined}
        collapsed={collapsed}
        onCollapseChange={setCollapsed}
        // onChangeTrigger="click"
        className="mpc-external-layout-content">
        {props.children}
        <SupplierChoose visible={supplierChooseVisible} onChangeVisible={setSupplierChooseVisible} />
        <ResetPwdModal visible={resetPwdVisible} onMenuVisibleChange={setResetPwdVisible} />
      </ProLayout.Content>
    </ProLayout>
    // </ProScreenAdapterProvider>
  );
}
